import React, { useState, useEffect } from "react";
import "./PastTimers.css";
import "../SearchContainer.css";
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Button,
  Pagination,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import dayjs from "dayjs";
import { useTheme } from "../../../context/ThemeContext";

const PastTimers = ({ pastTimersToggle }) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [checkIn, setCheckIn] = useState([]);
  const [searchName, setSearchName] = useState("");
  const [searchDate, setSearchDate] = useState(dayjs());
  const [loading, setLoading] = useState(false);
  const PAGE_SIZE = 50;
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchMode, setSearchMode] = useState("name"); // 'name' or 'date'
  const [timerMode, setTimerMode] = useState("all"); // all | over | defaulters

  const fetchTimers = async (pageToFetch = currentPage) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchMode === "name" && searchName && searchName.trim()) {
        params.append("name", searchName.trim());
      }
      if (searchMode === "date") {
        params.append("date", searchDate.format("YYYY-MM-DD"));
      }
      if (timerMode !== "all") {
        params.append("mode", timerMode);
      }
      params.append("page", String(pageToFetch));
      params.append("limit", String(PAGE_SIZE));

      const response = await fetch(
        `https://hr-backend-72v1.onrender.com/getAllTimers?${params.toString()}`
      );
      if (!response.ok) throw new Error("Failed to fetch");
      const data = await response.json();

      // New backend returns { timers, total }, but keep backwards-compat
      if (Array.isArray(data)) {
        setCheckIn(data);
        setTotalCount(data.length);
        setTotalPages(Math.max(1, Math.ceil(data.length / PAGE_SIZE)));
      } else {
        setCheckIn(data.timers || []);
        setTotalCount(data.total || 0);
        setTotalPages(
          Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE))
        );
      }
    } catch (error) {
      console.error("Error fetching timer data:");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pastTimersToggle) {
      fetchTimers(currentPage);
    }
  }, [pastTimersToggle, searchMode, timerMode]);

  const handlePageChange = (_event, value) => {
    setCurrentPage(value);
    fetchTimers(value);
  };

  const handleDropdownChange = (event) => {
    setCurrentPage(event.target.value); // Update current page from dropdown
  };

  const handleSearchByName = (event) => {
    setSearchName(event.target.value);
  };

  const handleSearchByDate = (date) => {
    setSearchDate(date);
  };

  const handleSearchModeChange = (event, newMode) => {
    if (newMode !== null) {
      setSearchMode(newMode);
      setCurrentPage(1);
    }
  };

  const handleFind = () => {
    setCurrentPage(1);
    fetchTimers(1);
  };
  // Filter group (All / Over Timers / Defaulters) - value-driven so the
  // active button highlights; drives the existing useEffect auto-fetch.
  const handleTimerModeChange = (event, newMode) => {
    if (newMode !== null) {
      setTimerMode(newMode);
      setCurrentPage(1);
    }
  };
  const refreshHandler = () => {
    setTimerMode("all");
    setSearchMode("name");
    setSearchName("");
    setSearchDate(dayjs());
    setCurrentPage(1);
  };
  
  
  

  return (
    <div>
      <div
        className="search-container"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div class="night">
          <div class="star"></div>
          <div class="star"></div>
          <div class="star"></div>
          <div class="star"></div>
          <div class="star"></div>
        </div>

        <div
          className="search-fields"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <div className="searchControls">
            {/* How to search - swaps the input below */}
            <div className="searchGroup">
              <span className="searchGroupLabel">Search by</span>
              <ToggleButtonGroup
                color="primary"
                value={searchMode}
                exclusive
                onChange={handleSearchModeChange}
                className="searchSeg"
              >
                <ToggleButton value="name">Name</ToggleButton>
                <ToggleButton value="date">Date</ToggleButton>
              </ToggleButtonGroup>
            </div>

            {/* What to show - server-side filter on the list */}
            <div className="searchGroup">
              <span className="searchGroupLabel">Filter</span>
              <ToggleButtonGroup
                color="primary"
                value={timerMode}
                exclusive
                onChange={handleTimerModeChange}
                className="searchSeg"
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="over" title="Sessions longer than 9 hours">
                  Over Timers
                </ToggleButton>
                <ToggleButton value="defaulters" title="No check-out recorded">
                  Defaulters
                </ToggleButton>
              </ToggleButtonGroup>
              <span className="searchGroupHint">
                Over Timers = over 9 hrs · Defaulters = no check-out
              </span>
            </div>

            <button
              type="button"
              className="searchReset"
              onClick={refreshHandler}
              title="Reset search & filters"
              aria-label="Reset search and filters"
            >
              <ReplayIcon fontSize="small" />
              <span>Reset</span>
            </button>
          </div>

          <div className="searchInputRow">
            {searchMode === "name" ? (
              <TextField
                className="searchInput"
                value={searchName}
                onChange={handleSearchByName}
                label="Search by name"
                variant="outlined"
              />
            ) : (
              <TextField
                className="searchInput"
                label="Search by date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={searchDate.format("YYYY-MM-DD")}
                onChange={(e) => handleSearchByDate(dayjs(e.target.value))}
              />
            )}
            <Button
              onClick={handleFind}
              variant="outlined"
              className="searchFindBtn"
              sx={{
                color: "var(--accent)",
                minWidth: "120px",
                height: "40px",
                borderColor: "rgba(212,175,55,0.4)",
                "&:hover": {
                  borderColor: "rgba(212,175,55,0.7)",
                  backgroundColor: "rgba(212,175,55,0.08)",
                },
              }}
            >
              Find
            </Button>
          </div>
        </div>
      </div>

      <TableContainer component={Paper} className="timerContainer">
        <h4 className="myTableHeader animate__animated animate__lightSpeedInLeft">
          ONLINE HISTORY
        </h4>
        <Table sx={{ minWidth: 650 }} className="leaveTable">
          <TableHead>
            <TableRow>
              <TableCell className="myTableHeadCells">
                <span className="myTableHeadSpan">Name</span>
              </TableCell>
              <TableCell className="myTableHeadCells">
                <span className="myTableHeadSpan">Email</span>
              </TableCell>
              <TableCell className="myTableHeadCells">
                <span className="myTableHeadSpan">Date</span>
              </TableCell>
              <TableCell className="myTableHeadCells">
                <span className="myTableHeadSpan">Check In</span>
              </TableCell>
              <TableCell className="myTableHeadCells" align="right">
                <span className="myTableHeadSpan">Check Out</span>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {checkIn.map((c) => (
              <TableRow key={c._id}>
                <TableCell sx={{ fontSize: "large" }}>{c.name}</TableCell>
                <TableCell sx={{ fontSize: "large" }}>{c.email}</TableCell>
                <TableCell sx={{ fontSize: "large" }}>
                  {new Date(c.checkIn).toISOString().split("T")[0]}
                </TableCell>
                <TableCell sx={{ fontSize: "large" }}>
                  {new Intl.DateTimeFormat("en-US", {
                    hour: "numeric",
                    minute: "numeric",
                    hour12: true,
                    timeZone: "UTC",
                  }).format(new Date(c.checkIn))}
                </TableCell>
                <TableCell className="timerCell" align="right">
                  {c.checkOut
                    ? new Intl.DateTimeFormat("en-US", {
                        hour: "numeric",
                        minute: "numeric",
                        hour12: true,
                        timeZone: "UTC",
                      }).format(new Date(c.checkOut))
                    : "-----"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "20px",
            margin: "20px 0",
          }}
        >
          {totalPages > 1 ? (
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
            />
          ) : null}
        </div>
      </TableContainer>
    </div>
  );
};

export default PastTimers;
